import React, { useState, useEffect, useCallback, Fragment, useRef } from "react";
import {
  ChatBubbleLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  PlusIcon,
  StarIcon as StarOutlineIcon,
  DocumentTextIcon,
  ClockIcon,
  PencilIcon,
  // CalenderDaysIcon,
  // DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { Popover, PopoverButton, PopoverPanel, Transition, Dialog, Portal } from "@headlessui/react";
import { useLeadOptimisticState } from 'contexts/LeadOptimisticStateContext';
import { addLeadNote } from "services/notesService";
import { getLead, counselorVerifyPhoneEmail, bulkUpdateLeadStage, getLeadJourneyIndicators, getRelatedLeads } from "services/leadsService";
import { getApplicationForms } from "services/paymentServices";
import { createFollowUp } from "services/followUpService";
import { toast } from "sonner";
import { transformLeadDetailsApiData } from "utils/leadDataTransforms";
import CallModal from "../LeadActions/CallModal";
import EmailMessageModal from "../LeadActions/EmailMessageModal";
import SMSMessageModal from "../LeadActions/SMSMessageModal";
import WhatsAppMessageModal from "../LeadActions/WhatsAppMessageModal";
import FollowUpScheduleModal from "../LeadActions/FollowUpScheduleModal";
import TimelineView from "components/leads/LeadDetails/TimeLineView";
import LeadDetailsForm from "components/leads/LeadDetails/LeadForm";
import NotesTab from "components/leads/LeadDetails/NotesTab";
import PaymentDetailsTab from "components/leads/LeadDetails/PaymentDetailsTab";
import ApplicationFormViewTab from "components/leads/LeadDetails/ApplicationFormViewTab";
import QuickLeadModal from "../LeadActions/QuickLeadModal";
import ApplicationFormModal from "../LeadActions/ApplicationFormModal";
import StageChangeModal from "../LeadActions/StageChangeModal";
import SetPriorityModal from "../LeadActions/SetPriorityModal";
import SetInteractionLevelModal from "../LeadActions/SetInteractionLevelModal";
import { Circlebar, Button, Badge } from "components/ui";
import { calculatePriority } from "../../../utils/priorityCalculator";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "U";
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
};

const getLeadFieldValue = (lead, label) => {
  if (!lead?.LeadFieldCategories?.length) return null;

  const normalizedLabel = label.toLowerCase();
  const allFields = lead.LeadFieldCategories.flatMap((category) => category.Fields || []);

  const field = allFields.find((fieldItem) => {
    const fieldLabel = (fieldItem.FieldLabel || fieldItem.FieldName || "").toLowerCase();
    return fieldLabel.includes(normalizedLabel) || (fieldItem.FieldName || "").toLowerCase().includes(normalizedLabel);
  });

  return field?.FieldValue || field?.FieldValueText || field?.FieldValueLabel || null;
};


export default function LeadRightDrawerContent({ user, onClose, onViewDetails, hideTabs = false, hideHeader = false, onRefresh, onDataFetched, initialLeadDetails = null, isParentFetching = false, entityName, initialTab }) {

  const [activeTab, setActiveTab] = useState(initialTab || (hideTabs ? "details" : "notes"));

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isEmailMessageModal, setIsEmailMessageModal] = useState(false);
  const [isSmsMessageModal, setIsSmsMessageModal] = useState(false);
  const [isWhatsAppMessageModal, setIsWhatsAppMessageModal] = useState(false);
  const [isFollowUpScheduleModal, setIsFollowUpScheduleModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [isImportantForNewNote, setIsImportantForNewNote] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isVerifying, setIsVerifying] = useState({ phone: false, email: false });
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  // ✅ Initialize from prop if provided — skips fetch entirely
  const [leadDetails, setLeadDetails] = useState(initialLeadDetails || null);
  const [primaryLeadDetails, setPrimaryLeadDetails] = useState(initialLeadDetails || null);
  const [isLeadDetailsLoading, setIsLeadDetailsLoading] = useState(false);
  const [leadDetailsError, setLeadDetailsError] = useState(null);
  const [showQuickLeadModal, setShowQuickLeadModal] = useState(false);
  const [showLeadEditModal, setShowLeadEditModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showInteractionLevelModal, setShowInteractionLevelModal] = useState(false);
  const [selectedFormForApplication, setSelectedFormForApplication] = useState(null);
  const [isComingFromPaymentFlow, setIsComingFromPaymentFlow] = useState(false);
  const [progressData, setProgressData] = useState({});
  const [showVerifyModal, setShowVerifyModal] = useState({ isOpen: false, type: null }); // type: 'phone' or 'email'
  const [relatedLeads, setRelatedLeads] = useState([]);
  const originalLeadId = user?.id || user?.LeadId || user?.leadId;
  const [activeLeadId, setActiveLeadId] = useState(originalLeadId || null);
  const isContactOnly = user?.isContactOnly || false;
  // Reset activeLeadId when user changes
  useEffect(() => {
    setActiveLeadId(originalLeadId || null);
    setPrimaryLeadDetails(null);
  }, [originalLeadId]);

  // Fetch related leads for the contact
  useEffect(() => {
    if (originalLeadId) {
      getRelatedLeads(originalLeadId)
        .then((res) => {
          if (res?.success && Array.isArray(res.data)) {
            setRelatedLeads(res.data || []);
          }
        })
        .catch((err) => console.error("Error fetching related leads in summary drawer:", err));
    }
  }, [originalLeadId]);

  // Ensure active lead matches the clicked lead in related leads
  useEffect(() => {
    if (relatedLeads.length > 0) {
      const uniqueRelatedLeads = Array.from(
        new Map(relatedLeads.map((item) => [item.Id || item.id, item])).values()
      );
      if (uniqueRelatedLeads.length > 0) {
        const targetId = activeLeadId || originalLeadId;
        const matchingLead = uniqueRelatedLeads.find((item) =>
          (targetId && String(item.Id || item.id).toLowerCase() === String(targetId).toLowerCase()) ||
          (user?.DisplayLeadId && String(item.DisplayLeadId || item.displayLeadId) === String(user.DisplayLeadId))
        );
        if (matchingLead) {
          const matchId = matchingLead.Id || matchingLead.id;
          if (matchId && String(matchId) !== String(activeLeadId)) {
            setActiveLeadId(matchId);
          }
        } else if (!activeLeadId) {
          const firstLeadId = uniqueRelatedLeads[0].Id || uniqueRelatedLeads[0].id;
          if (firstLeadId) {
            setActiveLeadId(firstLeadId);
          }
        }
      }
    }
  }, [originalLeadId, relatedLeads, user?.DisplayLeadId]);

  const refreshProgressJourney = useCallback(async (forceRefresh = false) => {
    const leadId = activeLeadId || user?.id || user?.LeadId || user?.leadId;
    if (!leadId) return;
    try {
      const response = await getLeadJourneyIndicators(leadId, forceRefresh);
      if (response.success && response.data) {
        setProgressData(response.data);
      }
    } catch (err) {
      console.error('Error refreshing progress journey:', err);
    }
  }, [user, activeLeadId]);

  useEffect(() => {
    if (activeTab === 'application' && (user?.id || user?.LeadId || user?.leadId || activeLeadId)) {
      refreshProgressJourney(true);
    }
  }, [activeTab, user, activeLeadId, refreshProgressJourney]);

  const relatedLeadsScrollRef = useRef(null);
  const [isRelatedLeadsScrollable, setIsRelatedLeadsScrollable] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (relatedLeadsScrollRef.current) {
        setIsRelatedLeadsScrollable(
          relatedLeadsScrollRef.current.scrollWidth > relatedLeadsScrollRef.current.clientWidth
        );
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [relatedLeads]);

  const scrollRelatedLeads = (direction) => {
    if (relatedLeadsScrollRef.current) {
      const scrollAmount = direction === 'left' ? -150 : 150;
      relatedLeadsScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const {
    localInteractionLevel, localPriority, localStage, localSubStage,
    setLocalInteractionLevel, setLocalPriority, setLocalStage, setLocalSubStage
  } = useLeadOptimisticState();

  // ✅ Base lead for active tab operations (below history tabs)
  const baseLead = (hideTabs && user) ? user : (leadDetails || primaryLeadDetails || user);

  // ✅ Primary base lead for top header & contact row (should always track the active/selected lead tab)
  const primaryBaseLead = leadDetails || primaryLeadDetails || user;

  const primaryLead = primaryBaseLead ? {
    ...primaryBaseLead,
    ...(localInteractionLevel ? { InteractionLevelName: localInteractionLevel, LeadStatus: localInteractionLevel } : {}),
    ...(localPriority ? { Priority: localPriority, priority: localPriority } : {}),
    ...(localStage ? { stage: localStage, StageName: localStage } : {}),
    ...(localSubStage ? { subStage: localSubStage, SubStageName: localSubStage } : {})
  } : null;

  // Apply optimistic overrides so all child tabs (like LeadDetailsForm) see the instant updates
  const lead = baseLead ? {
    ...baseLead,
    ...(localInteractionLevel && activeLeadId === null ? { InteractionLevelName: localInteractionLevel, LeadStatus: localInteractionLevel } : {}),
    ...(localPriority && activeLeadId === null ? { Priority: localPriority, priority: localPriority } : {}),
    ...(localStage && activeLeadId === null ? { stage: localStage, StageName: localStage } : {}),
    ...(localSubStage && activeLeadId === null ? { subStage: localSubStage, SubStageName: localSubStage } : {})
  } : null;

  const currentLeadData = leadDetails || primaryBaseLead || user;
  const isDuplicateLead = Boolean(
    leadDetails?.BasicInformation?.ParentLeadId ||
    leadDetails?.BasicInformation?.IsDuplicate ||
    currentLeadData?.ParentLeadId ||
    currentLeadData?.parentLeadId ||
    currentLeadData?.IsDuplicate ||
    currentLeadData?.isDuplicate ||
    (String(activeLeadId || originalLeadId).toLowerCase() === String(originalLeadId).toLowerCase() && 
      (user?.IsDuplicate || user?.isDuplicate || user?.ParentLeadId || user?.parentLeadId))
  );

  const activeRelatedLead = relatedLeads.find(
    (r) => String(r.Id || r.id).toLowerCase() === String(activeLeadId || originalLeadId).toLowerCase()
  );
  const isApplicantLead = Boolean(
    activeRelatedLead?.IsApplication ??
    activeRelatedLead?.isApplication ??
    leadDetails?.BasicInformation?.IsApplication ??
    leadDetails?.BasicInformation?.isApplication ??
    leadDetails?.IsApplication ??
    leadDetails?.isApplication ??
    currentLeadData?.IsApplication ??
    currentLeadData?.isApplication ??
    (String(activeLeadId || originalLeadId).toLowerCase() === String(originalLeadId).toLowerCase()
      ? (lead?.IsApplication ?? lead?.isApplication ?? user?.IsApplication ?? user?.isApplication)
      : false)
  );

  useEffect(() => {
    if (isDuplicateLead && (activeTab === "application" || activeTab === "payment")) {
      setActiveTab("notes");
    }
  }, [isDuplicateLead, activeTab]);

  // ✅ Stable ref for onDataFetched — prevents useCallback from recreating when prop changes
  const onDataFetchedRef = React.useRef(onDataFetched);
  React.useEffect(() => { onDataFetchedRef.current = onDataFetched; }, [onDataFetched]);

  // ✅ Stable ref for initialLeadDetails — prevents useCallback from recreating when parent updates
  const initialLeadDetailsRef = React.useRef(initialLeadDetails);
  React.useEffect(() => { initialLeadDetailsRef.current = initialLeadDetails; }, [initialLeadDetails]);

  // ✅ Deduplication: track the last lead ID we fetched to avoid repeated API calls
  const lastFetchedLeadId = React.useRef(
    initialLeadDetails
      ? (initialLeadDetails?.id || initialLeadDetails?.LeadId || initialLeadDetails?.leadId || null)
      : null
  );
  const isFetchingRef = React.useRef(false);

  // ✅ Track visited tabs for lazy persistent mounting
  const [visitedTabs, setVisitedTabs] = useState(new Set([activeTab || initialTab || "notes"]));

  useEffect(() => {
    if (activeTab) {
      setVisitedTabs(prev => new Set([...prev, activeTab]));
    }
  }, [activeTab]);

  const fetchLeadDetails = useCallback(async (forceRefresh = false, silent = false) => {
    const leadId = activeLeadId || user?.id || user?.LeadId || user?.leadId;
    if (!leadId) return;

    // ✅ Skip if parent is already fetching (important for mobile prefetch)
    if (isParentFetching && !forceRefresh) return;

    // ✅ Optimization for Mobile: Skip fetch if we are hidden (not in summary mode) 
    // and this is just the mount-fetch. DetailsView handles its own fetch.
    const isMobile = window.innerWidth < 768;
    if (isMobile && hideTabs && !forceRefresh && !initialLeadDetailsRef.current) return;

    // ✅ Skip if we already fetched this exact lead (unless explicitly refreshed)
    if (!forceRefresh && String(lastFetchedLeadId.current) === String(leadId)) return;
    // ✅ Skip if a fetch is already in progress
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    lastFetchedLeadId.current = leadId;
    if (!silent) setIsLeadDetailsLoading(true);
    setLeadDetailsError(null);

    try {
      if (silent) {
        // Give the backend a moment to update before we refetch
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      const result = await getLead(leadId, true);
      if (result.success && result.data?.data) {
        const transformedData = transformLeadDetailsApiData(result.data.data, leadId);
        setLeadDetails(transformedData);
        // Set primaryLeadDetails if fetching original lead
        if (!activeLeadId || String(leadId) === String(originalLeadId)) {
          setPrimaryLeadDetails(transformedData);
          if (onDataFetchedRef.current) {
            onDataFetchedRef.current(transformedData);
          }
        }
      } else {
        setLeadDetails(null);
        setLeadDetailsError(result.error || "Failed to load lead details");
      }
    } catch (err) {
      console.error("Lead summary fetch error:", err);
      setLeadDetails(null);
      setLeadDetailsError(err.message || "Failed to load lead details");
    } finally {
      setIsLeadDetailsLoading(false);
      isFetchingRef.current = false;
    }
  }, [user?.id, user?.LeadId, user?.leadId, activeLeadId, isParentFetching, hideTabs, originalLeadId]);

  // ✅ Sync with initialLeadDetails changes
  useEffect(() => {
    if (initialLeadDetails && !leadDetails) {
      setLeadDetails(initialLeadDetails);
      lastFetchedLeadId.current = initialLeadDetails?.id || initialLeadDetails?.LeadId || initialLeadDetails?.leadId;
    }
  }, [initialLeadDetails, leadDetails]);

  useEffect(() => {
    fetchLeadDetails();
  }, [fetchLeadDetails]);

  // When user explicitly refreshes, force a re-fetch even for the same lead ID
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchLeadDetails(true, true);
    }
  }, [refreshTrigger]);

  // ✅ Reset optimistic state and clear stale details when switching leads
  useEffect(() => {
    const leadId = user?.id || user?.LeadId || user?.leadId;
    if (leadId) {
      // Clear optimistic overrides
      setLocalStage(null);
      setLocalSubStage(null);
      setLocalPriority(null);
      setLocalInteractionLevel(null);

      // Clear stale detailed data so we fallback to 'user' prop (summary data) until fetch completes
      setLeadDetails(null);
      lastFetchedLeadId.current = null;
    }
  }, [user?.id, user?.LeadId, user?.leadId, setLocalStage, setLocalSubStage, setLocalPriority, setLocalInteractionLevel]);

  const handleVerifyPhoneEmail = async (isPhone, close) => {
    const fieldType = isPhone ? "phone" : "email";
    const fieldValue = isPhone ? phoneNumber : emailAddress;

    if (!fieldValue || fieldValue === "N/A") {
      toast.error(`No ${fieldType} found to verify`);
      if (close) close();
      return;
    }

    setIsVerifying({ ...isVerifying, [fieldType]: true });

    try {
      const payload = {
        leadId: lead?.id || lead?.LeadId,
        fieldValue: fieldValue,
        isPhone: isPhone,
        isEmail: !isPhone
      };

      const result = await counselorVerifyPhoneEmail(payload);

      if (result.success) {
        toast.success(result.message || `${isPhone ? 'Phone' : 'Email'} verified successfully`);
        if (onRefresh) onRefresh();
        // Also refresh local data forcefully and silently
        fetchLeadDetails(true, true);
      } else {
        toast.error(result.message || `Failed to verify ${fieldType}`);
      }
    } catch (error) {
      console.error(`Error verifying ${fieldType}:`, error);
      toast.error(`Failed to verify ${fieldType}`);
    } finally {
      setIsVerifying({ ...isVerifying, [fieldType]: false });
      if (close) close();
    }
  };


  const displayLeadId = lead?.displayId || lead?.DisplayLeadId || lead?.id || lead?.LeadId || lead?.leadId;
  const phoneNumber = lead?.MobileNo || lead?.mobile || lead?.phone || getLeadFieldValue(lead, "mobile") || "N/A";
  const emailAddress = lead?.EmailId || lead?.email || getLeadFieldValue(lead, "email") || "N/A";
  const stageName = lead?.stage || lead?.StageName || "N/A";
  const substageName = lead?.subStage || lead?.SubStageName || lead?.substage || lead?.SubstageName || "N/A";

  // Primary lead fields for upper header & contact row (always stays as current/original lead)
  const primaryLeadName = primaryLead?.FullName || primaryLead?.name || primaryLead?.LeadName || "Unknown User";
  let primaryDisplayLeadId = primaryLead?.displayId || primaryLead?.DisplayLeadId;
  if (!primaryDisplayLeadId) {
    const fallbackId = primaryLead?.id || primaryLead?.LeadId || primaryLead?.leadId;
    if (fallbackId && String(fallbackId).length < 20) {
      primaryDisplayLeadId = fallbackId;
    }
  }
  const primaryPhoneNumber = primaryLead?.MobileNo || primaryLead?.mobile || primaryLead?.phone || getLeadFieldValue(primaryLead, "mobile") || "N/A";
  const primaryEmailAddress = primaryLead?.EmailId || primaryLead?.email || getLeadFieldValue(primaryLead, "email") || "N/A";
  const primaryStageName = primaryLead?.stage || primaryLead?.StageName || "N/A";
  const primarySubstageName = primaryLead?.subStage || primaryLead?.SubStageName || primaryLead?.substage || primaryLead?.SubstageName || "N/A";
  const primaryLeadSource =
    primaryLead?.LeadSourceName ||
    primaryLead?.SourceDetails?.LeadSource ||
    primaryLead?.source ||
    primaryLead?.LeadSource ||
    primaryLead?.SourceName ||
    primaryLead?.sourceName ||
    user?.LeadSourceName ||
    user?.SourceName ||
    user?.source ||
    user?.leadSource ||
    "N/A";

  // Enforce read-only status for Applicants and Applications
  const isReadOnly = primaryLead?.IsStudent;

  const isChangeStageRestricted = (primaryStageName?.toLowerCase() === "closed" || primaryStageName?.toLowerCase() === "close") &&
    (primarySubstageName?.toLowerCase() === "win" || primarySubstageName?.toLowerCase() === "registered");


  const getLeadScoreColor = (score) => {
    if (!score || score === "Unscored") return "neutral";
    const priority = calculatePriority(score);
    switch (priority?.id) {
      case "hot":
      case "urgent":
        return "error";
      case "high":
        return "error";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "neutral";
    }
  };

  const getPriorityStyles = (priority) => {
    const p = priority?.toLowerCase();
    if (p === 'high' || p === 'urgent' || p === 'hot') {
      return "bg-[#fef2f2] text-[#ef4444] hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400";
    }
    if (p === 'medium') {
      return "bg-yellow-50 text-yellow-600 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400";
    }
    if (p === 'low') {
      return "bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400";
    }
    return "bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400";
  };

  const handleSubmitNote = async () => {
    if (!newNoteText.trim() || isDuplicateLead) return;

    setIsSubmittingNote(true);
    try {
      const leadId = user?.id || user?.LeadId || user?.leadId;
      const result = await addLeadNote(leadId, newNoteText, isImportantForNewNote);
      if (result.success) {
        toast.success("Note added successfully!");
        setNewNoteText("");
        setIsImportantForNewNote(false);
        setIsAddingNote(false);
        setRefreshTrigger((prev) => prev + 1);
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
      } else {
        toast.error(result.error || "Failed to add note");
      }
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleScheduleFollowUp = async (followUpData) => {
    try {
      const result = await createFollowUp(followUpData);
      if (result.success) {
        if (onRefresh) onRefresh();
        setRefreshTrigger(prev => prev + 1);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error scheduling follow-up:", error);
      return false;
    }
  };

  const handlestageUpdate = async (stageData) => {
    try {
      const targetLeadId = primaryLead?.id || primaryLead?.LeadId || primaryLead?.leadId || user?.id || user?.LeadId || user?.leadId;
      const result = await bulkUpdateLeadStage({
        leadIds: [targetLeadId],
        stageId: stageData.stageId,
        subStageId: stageData.subStageId,
        remarks: stageData.notes || "",
        leadScore: stageData.leadScore,
      });

      if (result.success) {
        // Check for formId in response data to open application form automatically
        let formIdToOpen = null;
        if (result.data) {
          try {
            const parsedData = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
            if (parsedData.formId) {
              formIdToOpen = parsedData.formId;
            }
          } catch (error) {
            console.error("Error parsing formId from response:", error);
          }
        }

        const stageName = stageData?.leadStage || "";
        const isClosedStage = stageName.toLowerCase().includes("close") || stageName.toLowerCase().includes("won") || stageName.toLowerCase().includes("win");

        if (isClosedStage) {
          if (!formIdToOpen) {
            try {
              const freshLeadRes = await getLead(targetLeadId, true);
              if (freshLeadRes.success && freshLeadRes.data?.data) {
                const freshLead = freshLeadRes.data.data;
                formIdToOpen = freshLead?.FormId || freshLead?.formId || freshLead?.ApplicationFormId || freshLead?.applicationFormId ||
                  freshLead?.BasicInformation?.ApplicationFormId || freshLead?.BasicInformation?.applicationFormId ||
                  freshLead?.BasicInformation?.FormId || freshLead?.BasicInformation?.formId;
              }
            } catch (fetchError) {
              console.error("Failed to fetch fresh lead details in drawer:", fetchError);
            }
          }

          let resolvedFormId = formIdToOpen || primaryLead?.FormId || primaryLead?.formId || primaryLead?.ApplicationFormId || primaryLead?.applicationFormId;

          if (!resolvedFormId) {
            try {
              const formsRes = await getApplicationForms();
              if (formsRes.success && Array.isArray(formsRes.data) && formsRes.data.length > 0) {
                resolvedFormId = formsRes.data[0].FormId || formsRes.data[0].formId;
              }
            } catch (formsError) {
              console.error("Failed to fetch fallback application forms in drawer:", formsError);
            }
          }

          if (resolvedFormId) {
            setSelectedFormForApplication({ FormId: resolvedFormId });

            // Delay opening to allow confirmation modal flow to complete
            setTimeout(() => {
              setShowQuickLeadModal(true);
            }, 500);
          }
        }
        setLocalStage(stageData.leadStage);
        setLocalSubStage(stageData.subStage);

        // Optimistic update
        setLeadDetails(prev => prev ? ({
          ...prev,
          stage: stageData.leadStage,
          subStage: stageData.subStage
        }) : prev);
        toast.success("Stage updated successfully!");
        setRefreshTrigger((prev) => prev + 1);
        if (onRefresh) onRefresh();
        return true;
      } else {
        toast.error(result.error || "Failed to update stage");
        return false;
      }
    } catch (error) {
      console.error("Error updated stage:", error);
      toast.error("An error occurred while updated stage");
      return false;
    }
  };

  /*
  // Handle Access Denied / API Errors
  if (leadDetailsError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white dark:bg-dark-900">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-[16px] font-bold text-gray-900 dark:text-dark-100">Access Restricted</h3>
        <p className="mt-2 text-[13px] text-gray-500 dark:text-dark-400 max-w-[250px]">
          You are not assigned to this Lead, or you do not have permission to view this data.
        </p>
      </div>
    );
  }
  */

  if (!primaryLead) return null;
  /*
  // 1. PRIORITIZE ERROR: If the API told us "Forbidden" or "Not Found", show it immediately
  if (leadDetailsError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white dark:bg-dark-900">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 mb-6">
          <ExclamationTriangleIcon className="w-10 h-10 text-red-500" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-dark-100">Access Restricted</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-dark-400 max-w-[280px]">
          You are not assigned to this lead or do not have permission to view this data.
        </p>
        <Button variant="outlined" className="mt-6" onClick={onClose}>
          Close Drawer
        </Button>
      </div>
    );
  }
  */

  // 2. LOADING STATE: Only show spinner if there is no error and no data yet
  if (isLeadDetailsLoading && !leadDetails) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white dark:bg-dark-900">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
          <p className="text-xs text-gray-500 animate-pulse">Loading lead info...</p>
        </div>
      </div>
    );
  }

  /*
  // 3. DATA CHECK: If not loading but leadDetails is still empty, it's an implicit error
  if (!isLeadDetailsLoading && !leadDetails) {
     setLeadDetailsError("Lead not found"); // Trigger the error UI above
     return null;
  }
  */

  // 4. SUCCESS: Normal rendering continues...

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-dark-900">
      <div className="px-3 sm:px-5 py-2 sm:py-2.5 bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-800 shrink-0">
        <div className="flex justify-between items-center sm:items-start gap-2 sm:gap-4">
          <div className="flex gap-2.5 sm:gap-3 items-center sm:items-start min-w-0 flex-1">
            <div className="flex items-center justify-center w-10 h-10 sm:w-[46px] sm:h-[46px] rounded-full bg-primary-50 text-primary-600 text-[15px] sm:text-[16px] font-semibold shrink-0">
              {getInitials(primaryLeadName)}
            </div>
            <div className="flex flex-col min-w-0 flex-1 justify-center mt-0 sm:mt-0.5">
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap mb-0.5 sm:mb-1">
                <h2 className="text-sm sm:text-[15px] font-bold text-gray-900 dark:text-dark-100 leading-tight sm:leading-none truncate">{primaryLeadName}</h2>
                <div className="flex items-center gap-1 sm:gap-1.5">
                  {!isContactOnly && (
                    <>
                      <span
                        onClick={() => !isDuplicateLead && setShowInteractionLevelModal(true)}
                        className={clsx(
                          "px-1 sm:px-1.5 py-[2px] text-[8px] sm:text-[9px] font-bold rounded bg-[#eff6ff] text-[#2563eb] uppercase tracking-wide leading-none transition-colors",
                          isDuplicateLead ? "cursor-default opacity-80" : "cursor-pointer hover:bg-blue-100"
                        )}
                      >
                        {primaryLead?.LeadStatus || primaryLead?.InteractionLevelName || "COLD"}
                      </span>
                      <span
                        onClick={() => !isDuplicateLead && setShowPriorityModal(true)}
                        className={clsx(
                          "px-1 sm:px-1.5 py-[2px] text-[8px] sm:text-[9px] font-bold rounded uppercase tracking-wide leading-none transition-colors",
                          getPriorityStyles(primaryLead?.Priority || "HIGH"),
                          isDuplicateLead ? "cursor-default opacity-80" : "cursor-pointer"
                        )}
                      >
                        {primaryLead?.Priority || "HIGH"}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {/* Metadata Row - Compact on Mobile */}
              {!isContactOnly && (
                <div className="flex flex-col gap-1 sm:gap-1 mt-0.5">
                  {/* Row 1: ID and Lead Source */}
                  <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-[12px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {primaryDisplayLeadId && (
                      <>
                        <span className="font-medium">#{primaryDisplayLeadId}</span>
                        {primaryLeadSource && primaryLeadSource !== "N/A" && (
                          <div className="w-[1px] h-3 bg-gray-300 dark:bg-dark-600"></div>
                        )}
                      </>
                    )}
                    {primaryLeadSource && primaryLeadSource !== "N/A" && (
                      <span className="font-medium">{primaryLeadSource}</span>
                    )}
                  </div>

                  {/* Stage and Substage Pills */}
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5">
                    <div
                      onClick={(!isReadOnly && !isChangeStageRestricted && !isDuplicateLead) ? () => setShowStageModal(true) : undefined}
                      className={clsx(
                        "flex items-center gap-2",
                        (!isReadOnly && !isChangeStageRestricted && !isDuplicateLead)
                          ? "cursor-pointer"
                          : "cursor-default opacity-80"
                      )}
                    >
                      <span className="px-2.5 py-0.5 text-[9px] font-bold rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 uppercase tracking-wide">
                        {primaryStageName === "N/A" ? "NEW" : primaryStageName}
                      </span>
                      <span className="px-2.5 py-0.5 text-[9px] font-bold rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 uppercase tracking-wide">
                        {primarySubstageName}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2 shrink-0">
            {!isContactOnly && (
              <div className="flex flex-col items-center shrink-0">
                <Circlebar
                  value={primaryLead?.LeadScore === "Unscored" ? 0 : primaryLead?.LeadScore || primaryLead?.leadScore || 0}
                  size={11}
                  strokeWidth={8}
                  color={getLeadScoreColor(primaryLead?.LeadScore || primaryLead?.leadScore)}
                  className="mx-auto"
                >
                  <div className="text-center">
                    <div className="text-[10px] font-bold text-gray-900 dark:text-dark-100 leading-[10px]">
                      {primaryLead?.LeadScore === "Unscored" ? "--" : primaryLead?.LeadScore || primaryLead?.leadScore || 0}
                    </div>
                  </div>
                </Circlebar>
                <div className="text-[7px] font-medium text-gray-500 dark:text-gray-400 mt-0.5 uppercase tracking-wide">Score</div>
              </div>
            )}

            {(onViewDetails || onClose) && (
              <div className="flex items-center gap-0.5 ml-1">
                {onViewDetails && !isContactOnly && (
                  <button
                    onClick={() => onViewDetails(primaryLead)}
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-all cursor-pointer"
                    title="Expand"
                  >
                    <ArrowsPointingOutIcon className="w-5 h-5" />
                  </button>
                )}
                {onClose && (
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 hover:text-gray-700 dark:hover:text-gray-200 transition-all cursor-pointer"
                    title="Close"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Contact Row */}
        <div className="flex flex-col gap-2 mt-2 text-[12px] text-gray-600 dark:text-dark-300 min-w-0">
          <div className="flex items-center justify-between w-full p-1.5 px-3 border border-gray-200 dark:border-dark-700 rounded-xl min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 font-medium text-gray-800 dark:text-dark-100 min-w-0 flex-1">
              {/* Phone */}
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 shrink-0">
                <div className="p-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center group-hover:bg-blue-100 transition-colors shrink-0">
                  <PhoneIcon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                </div>

                {primaryPhoneNumber !== "N/A" ? (
                  <Popover className="relative inline-flex items-center min-w-0">
                    <PopoverButton
                      disabled={primaryLead?.IsStudent || isContactOnly || isDuplicateLead}
                      className={clsx(
                        "flex items-center gap-1.5 outline-none group/verify min-w-0",
                        primaryLead?.IsStudent && "opacity-60 cursor-not-allowed",
                        (isContactOnly || isDuplicateLead) && "cursor-default pointer-events-none"
                      )}
                    >
                      <span className="font-semibold tracking-tight truncate group-hover/verify:text-primary-600 dark:group-hover/verify:text-primary-400 transition-colors">
                        {primaryPhoneNumber}
                      </span>
                      <div className={clsx(
                        "w-1 h-1 rounded-full animate-pulse",
                        primaryLead?.PhoneVerified ? "bg-[#22c55e]" : "bg-[#ef4444]"
                      )}></div>
                    </PopoverButton>
                    <Transition as={Fragment} enter="transition ease-out duration-200" enterFrom="opacity-0 translate-y-2" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-2">
                      <Portal>
                        <PopoverPanel anchor={{ to: "bottom start", gap: 8 }} className="z-[99999] w-[300px] rounded-xl border border-gray-200 bg-white p-4 shadow-xl outline-none ring-1 ring-black/5 dark:border-dark-700 dark:bg-dark-800 dark:shadow-none">
                          {({ close }) => (
                            <>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-[12px] text-gray-700 dark:text-dark-200 font-medium leading-relaxed">
                                    Are you sure you want to {primaryLead?.PhoneVerified ? 'unverify' : 'verify'} this phone number?
                                  </p>
                                </div>
                              </div>
                              <div className="mt-4 flex gap-2 justify-end">
                                <Button onClick={() => close()} variant="outlined" color="neutral" size="sm" className="h-8 text-[11px]" disabled={isVerifying.phone}>Cancel</Button>
                                <Button onClick={() => handleVerifyPhoneEmail(true, close)} variant="filled" color={primaryLead?.PhoneVerified ? "error" : "primary"} size="sm" className="h-8 text-[11px] inline-flex items-center gap-1.5" disabled={isVerifying.phone}>
                                  {isVerifying.phone ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />Processing...</> : <><CheckBadgeIcon className="h-3.5 w-3.5" />{primaryLead?.PhoneVerified ? 'Unverify' : 'Verify'}</>}
                                </Button>
                              </div>
                            </>
                          )}
                        </PopoverPanel>
                      </Portal>
                    </Transition>
                  </Popover>
                ) : (
                  <span className="font-semibold tracking-tight truncate text-gray-400 dark:text-dark-600">N/A</span>
                )}
              </div>

              <div className="w-[1px] h-5 bg-gray-200 dark:bg-dark-700 shrink-0"></div>

              {/* Email */}
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                <div className="p-1.5 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center group-hover:bg-orange-100 transition-colors shrink-0">
                  <EnvelopeIcon className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
                </div>

                {primaryEmailAddress !== "N/A" ? (
                  <Popover className="relative inline-flex items-center min-w-0">
                    <PopoverButton
                      disabled={primaryLead?.IsStudent || isContactOnly || isDuplicateLead}
                      className={clsx(
                        "flex items-center gap-1.5 outline-none group/verify min-w-0",
                        primaryLead?.IsStudent && "opacity-60 cursor-not-allowed",
                        (isContactOnly || isDuplicateLead) && "cursor-default pointer-events-none"
                      )}
                    >
                      <span 
                        title={primaryEmailAddress}
                        className="font-semibold tracking-tight truncate group-hover/verify:text-primary-600 dark:group-hover/verify:text-primary-400 transition-colors"
                      >
                        {primaryEmailAddress}
                      </span>
                      <div className={clsx(
                        "w-1 h-1 rounded-full animate-pulse",
                        primaryLead?.EmailVerified ? "bg-[#22c55e]" : "bg-[#ef4444]"
                      )}></div>
                    </PopoverButton>
                    <Transition as={Fragment} enter="transition ease-out duration-200" enterFrom="opacity-0 translate-y-2" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-2">
                      <Portal>
                        <PopoverPanel anchor={{ to: "bottom start", gap: 8 }} className="z-[99999] w-[300px] rounded-xl border border-gray-200 bg-white p-4 shadow-xl outline-none ring-1 ring-black/5 dark:border-dark-700 dark:bg-dark-800 dark:shadow-none">
                          {({ close }) => (
                            <>
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-[12px] text-gray-700 dark:text-dark-200 font-medium leading-relaxed">
                                    Are you sure you want to {primaryLead?.EmailVerified ? 'unverify' : 'verify'} this email address?
                                  </p>
                                </div>
                              </div>
                              <div className="mt-4 flex gap-2 justify-end">
                                <Button onClick={() => close()} variant="outlined" color="neutral" size="sm" className="h-8 text-[11px]" disabled={isVerifying.email}>Cancel</Button>
                                <Button onClick={() => handleVerifyPhoneEmail(false, close)} variant="filled" color={primaryLead?.EmailVerified ? "error" : "primary"} size="sm" className="h-8 text-[11px] inline-flex items-center gap-1.5" disabled={isVerifying.email}>
                                  {isVerifying.email ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />Processing...</> : <><CheckBadgeIcon className="h-3.5 w-3.5" />{primaryLead?.EmailVerified ? 'Unverify' : 'Verify'}</>}
                                </Button>
                              </div>
                            </>
                          )}
                        </PopoverPanel>
                      </Portal>
                    </Transition>
                  </Popover>
                ) : (
                  <span className="font-semibold tracking-tight truncate text-gray-400 dark:text-dark-600">N/A</span>
                )}
              </div>
            </div>

            {/* Inline contact action icons (WhatsApp, SMS, Follow-up) */}
            {!isContactOnly && !isDuplicateLead && (
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <button
                  onClick={(e) => { e.stopPropagation(); setIsWhatsAppMessageModal(true); }}
                  title="WhatsApp"
                  className="text-emerald-500 hover:text-emerald-600 transition-colors"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4" />
                </button>
                <div className="w-[1px] h-3 bg-gray-200 dark:bg-dark-700"></div>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsSmsMessageModal(true); }}
                  title="SMS"
                  className="text-gray-400 dark:text-dark-400 hover:text-gray-600 transition-colors"
                >
                  <DevicePhoneMobileIcon className="w-4 h-4" />
                </button>
                <div className="w-[1px] h-3 bg-gray-200 dark:bg-dark-700"></div>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsFollowUpScheduleModal(true); }}
                  title="Schedule Follow-up"
                  className="text-pink-500 hover:text-pink-600 transition-colors"
                >
                  <ClockIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>


      </div>



      {/* Related Leads History Tabs */}
      {!isContactOnly && !hideTabs && (() => {
        const rawUniqueRelatedLeads = Array.from(
          new Map(relatedLeads.map((item) => [item.Id || item.id, item])).values()
        );

        if (rawUniqueRelatedLeads.length <= 1) return null;

        const isLeadDuplicate = (leadItem) => {
          const lId = String(leadItem.Id || leadItem.id || "").toLowerCase();
          const activeId = String(activeLeadId || originalLeadId || "").toLowerCase();
          const detailsBasic = (activeId === lId && leadDetails?.BasicInformation) ? leadDetails.BasicInformation : null;
          const userBasic = (String(user?.id || user?.LeadId || user?.leadId || "").toLowerCase() === lId) ? user : null;

          const isDupVal =
            leadItem.IsDuplicate ??
            leadItem.isDuplicate ??
            detailsBasic?.IsDuplicate ??
            userBasic?.IsDuplicate ??
            userBasic?.isDuplicate;

          const parentIdVal =
            leadItem.ParentLeadId ??
            leadItem.parentLeadId ??
            detailsBasic?.ParentLeadId ??
            userBasic?.ParentLeadId ??
            userBasic?.parentLeadId;

          // If explicitly marked as duplicate or has a parent lead, it is a duplicate
          if (
            isDupVal === true ||
            isDupVal === 1 ||
            isDupVal === "1" ||
            isDupVal === "true" ||
            Boolean(parentIdVal)
          ) {
            return true;
          }

          // If explicitly marked as NOT duplicate and has no parent lead, it's primary
          if (
            (isDupVal === false ||
             isDupVal === 0 ||
             isDupVal === "0" ||
             isDupVal === "false") &&
            !parentIdVal
          ) {
            return false;
          }

          // If it is the parent of any other lead in the list, it's primary
          const isParentOfAny = rawUniqueRelatedLeads.some((other) => {
            const pId = other.ParentLeadId || other.parentLeadId;
            return pId && String(pId).toLowerCase() === lId;
          });
          if (isParentOfAny) return false;

          // Fallback: if ParentLeadId is present and not matching this lead, it's duplicate
          if (parentIdVal) return true;

          return false;
        };

        const isLeadStudent = (leadItem) => {
          const lId = String(leadItem.Id || leadItem.id || '').toLowerCase();
          const activeId = String(activeLeadId || originalLeadId || '').toLowerCase();
          const detailsBasic = (activeId === lId && leadDetails?.BasicInformation) ? leadDetails.BasicInformation : null;
          const userBasic = (String(user?.id || user?.LeadId || user?.leadId || '').toLowerCase() === lId) ? user : null;

          const isStuVal =
            leadItem.IsStudent ??
            leadItem.isStudent ??
            leadItem.is_student ??
            detailsBasic?.IsStudent ??
            detailsBasic?.isStudent ??
            userBasic?.IsStudent ??
            userBasic?.isStudent;

          return Boolean(isStuVal === true || isStuVal === 1 || isStuVal === '1' || isStuVal === 'true');
        };

        // Primary lead always comes first, followed by duplicate leads
        const uniqueRelatedLeads = [...rawUniqueRelatedLeads].sort((a, b) => {
          const aDup = isLeadDuplicate(a);
          const bDup = isLeadDuplicate(b);
          if (!aDup && bDup) return -1;
          if (aDup && !bDup) return 1;
          const timeA = new Date(a.CreatedAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.CreatedAt || b.createdAt || 0).getTime();
          if (timeB !== timeA) return timeB - timeA;
          const dispA = Number(a.DisplayLeadId || a.displayLeadId || 0);
          const dispB = Number(b.DisplayLeadId || b.displayLeadId || 0);
          return dispB - dispA;
        });

        return (
          <div className="px-4 py-1.5 bg-white dark:bg-dark-900 shrink-0 relative">
            <div
              ref={relatedLeadsScrollRef}
              className="border border-gray-200 dark:border-dark-700 rounded-lg flex items-center overflow-x-auto custom-scrollbar p-1 divide-x divide-gray-200 dark:divide-dark-700"
            >
              {uniqueRelatedLeads.map((rLead, index) => {
                const rLeadId = rLead.Id || rLead.id;
                const currentActiveId = activeLeadId || originalLeadId;
                const isActive = String(currentActiveId).toLowerCase() === String(rLeadId).toLowerCase();
                const isDup = isLeadDuplicate(rLead);
                const rawId = rLead.DisplayLeadId || rLead.displayLeadId || rLead.DisplayId || rLead.displayId || "";
                const labelText = rawId
                  ? (String(rawId).startsWith('#') ? String(rawId) : `#${rawId}`)
                  : (rLead.FormName || rLead.formName || "Lead");
                const handleClick = () => setActiveLeadId(rLeadId);
                return (
                  <button
                    key={rLeadId}
                    onClick={handleClick}
                    className={clsx(
                      "px-3 py-1.5 flex flex-col items-center justify-center text-[11px] whitespace-nowrap transition-all cursor-pointer relative",
                      isActive
                        ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                        : "text-gray-500 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-800"
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={clsx("font-semibold", isActive ? "text-primary-600 dark:text-primary-400" : "")}>{labelText}</span>
                      {isDup ? (
                        <Badge color="warning" variant="filled" className="font-bold px-1.5 py-0 text-[9px] leading-tight" title="Duplicate">
                          D
                        </Badge>
                      ) : (
                        <Badge color="error" variant="filled" className="font-bold px-1.5 py-0 text-[9px] leading-tight" title="Primary">
                          P
                        </Badge>
                      )}
                    </div>
                    <span className="text-[9px] text-gray-400 mt-0.5">
                      {rLead.CreatedAt || rLead.createdAt ? new Date(rLead.CreatedAt || rLead.createdAt).toLocaleDateString() : ""}
                    </span>
                    {isActive && (
                      <div className="absolute -bottom-1 left-0 w-full h-[2px] bg-primary-600 rounded-t-sm"></div>
                    )}
                  </button>
                );
              })}

              {/* Pagination arrows */}
              {isRelatedLeadsScrollable && (
                <div className="flex items-center gap-1.5 px-2 ml-auto sticky right-0 bg-white dark:bg-dark-900 shrink-0 z-10 before:content-[''] before:absolute before:-left-4 before:top-0 before:bottom-0 before:w-4 before:bg-gradient-to-r before:from-transparent before:to-white dark:before:to-dark-900">
                  <button onClick={() => scrollRelatedLeads('left')} className="p-1 rounded bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 dark:bg-dark-800 dark:border-dark-700 dark:hover:bg-dark-700">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <button onClick={() => scrollRelatedLeads('right')} className="p-1 rounded bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 dark:bg-dark-800 dark:border-dark-700 dark:hover:bg-dark-700">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Duplicate Lead View-Only Banner */}
      {isDuplicateLead && (
        <div className="mx-4 mt-1.5 mb-1 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-center gap-2 text-amber-800 dark:text-amber-200 text-xs font-medium shrink-0">
          <svg className="w-4 h-4 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>Duplicate Lead (View Only) — Actions and modifications are disabled.</span>
        </div>
      )}

      {/* Applicant Lead Banner */}
      {!isDuplicateLead && isApplicantLead && (
        <div className="mx-4 mt-1.5 mb-1 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2 text-emerald-800 dark:text-emerald-200 text-xs font-medium shrink-0">
          <svg className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Applicant Lead — This lead has been converted to an application.</span>
        </div>
      )}

      {!hideTabs && !isContactOnly && (
        <div className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-800 shrink-0 mt-1">
          <div className="flex gap-4 px-5 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab("notes")}
              className={`py-2 flex items-center gap-1 text-[12px] font-semibold transition-all duration-200 border-b-[2.5px] whitespace-nowrap ${activeTab === "notes"
                ? "border-primary-600 text-primary-600 dark:text-primary-400"
                : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                }`}
            >
              <DocumentTextIcon className="w-3.5 h-3.5" />
              Notes
            </button>
            <button
              onClick={() => setActiveTab("details")}
              className={`py-2 flex items-center gap-1 text-[12px] font-semibold transition-all duration-200 border-b-[2.5px] whitespace-nowrap ${activeTab === "details"
                ? "border-primary-600 text-primary-600 dark:text-primary-400"
                : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              Details
            </button>
            <button
              onClick={() => setActiveTab("timeline")}
              className={`py-2 flex items-center gap-1 text-[12px] font-semibold transition-all duration-200 border-b-[2.5px] whitespace-nowrap ${activeTab === "timeline"
                ? "border-primary-600 text-primary-600 dark:text-primary-400"
                : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                }`}
            >
              <ClockIcon className="w-3.5 h-3.5" />
              Timeline
            </button>
            {!isDuplicateLead && (
              <>
                <button
                  onClick={() => setActiveTab("application")}
                  className={`py-2 flex items-center gap-1 text-[12px] font-semibold transition-all duration-200 border-b-[2.5px] whitespace-nowrap ${activeTab === "application"
                    ? "border-primary-600 text-primary-600 dark:text-primary-400"
                    : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                    }`}
                >
                  <DocumentTextIcon className="w-3.5 h-3.5" />
                  Application
                </button>
                <button
                  onClick={() => setActiveTab("payment")}
                  className={`py-2 flex items-center gap-1 text-[12px] font-semibold transition-all duration-200 border-b-[2.5px] whitespace-nowrap ${activeTab === "payment"
                    ? "border-primary-600 text-primary-600 dark:text-primary-400"
                    : "border-transparent text-[#6b7280] dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100"
                    }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                  Payment
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {isContactOnly ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50 dark:bg-dark-800">
          <div className="w-16 h-16 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
          </div>
          <h3 className="text-[16px] font-bold text-gray-900 dark:text-dark-100 mb-2">Basic Contact Record</h3>
          <p className="text-sm text-gray-500 dark:text-dark-400 max-w-sm">
            Import this contact to your leads list to unlock notes, details, timeline, and more CRM features.
          </p>
        </div>
      ) : (
        <>
          <div id="drawer-scroll-container" className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-1 bg-white dark:bg-dark-800 flex flex-col ${activeTab === "payment" ? "pb-2" : "pb-24"}`}>
            {/* Notes Tab */}
            {visitedTabs.has("notes") && (
              <div className={activeTab === "notes" ? "block flex-1 flex flex-col h-full mt-0" : "hidden"}>

                <style>{`
              .hide-native-add-btn > div > div:first-child > div:first-child {
                  display: none !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 {
                  display: flex !important;
                  flex-wrap: nowrap !important;
                  align-items: center !important;
                  justify-content: space-between !important;
                  padding: 0.25rem 0.5rem !important;
                  border-radius: 0.5rem !important;
                  gap: 0.25rem !important;
                  margin-bottom: 0.5rem !important;
              }
              .dark .hide-native-add-btn .rounded-xl.bg-gray-50 {
                  background-color: rgba(30, 41, 59, 0.5) !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 > div:first-child {
                  flex: 1 1 auto !important;
                  display: flex !important;
                  flex-wrap: nowrap !important;
                  justify-content: space-between !important;
                  align-items: center !important;
                  gap: 0.35rem !important;
                  min-width: 0 !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 label {
                  margin-right: 2px !important;
                  white-space: nowrap !important;
                  font-size: 11px !important;
              }
              .hide-native-add-btn .rounded-xl.bg-gray-50 .text-right.text-sm {
                  font-size: 10px !important;
                  opacity: 0.8 !important;
              }
              .hide-native-add-btn .react-select__control {
                  min-height: 30px !important;
                  height: 30px !important;
                  border-radius: 0.4rem !important;
                  background-color: transparent !important;
              }
              .dark .hide-native-add-btn .react-select__control {
                  border-color: #334155 !important;
              }
              .dark .hide-native-add-btn .react-select__single-value {
                  color: #f1f5f9 !important;
              }
              .dark .hide-native-add-btn .react-select__menu {
                  background-color: #1e293b !important;
                  border-color: #334155 !important;
              }
              .dark .hide-native-add-btn .react-select__option {
                  background-color: transparent !important;
                  color: #cbd5e1 !important;
              }
              .dark .hide-native-add-btn .react-select__option--is-focused {
                  background-color: #334155 !important;
              }
              .hide-native-add-btn .react-select__value-container {
                  padding: 0 8px !important;
                  font-size: 0.75rem !important;
              }
              .hide-native-add-btn .react-select__indicators {
                  height: 30px !important;
              }
              .hide-native-add-btn .bg-gray-50 label,
              .hide-native-add-btn .bg-gray-50 .text-sm {
                  font-size: 0.75rem !important;
              }
              .dark .hide-native-add-btn .bg-gray-50 label {
                  color: #94a3b8 !important;
              }
              .hide-native-add-btn .prose p {
                  font-size: 0.8125rem !important;
                  line-height: 1.2rem !important;
              }
              .hide-native-add-btn .mb-4 { margin-bottom: 0.25rem !important; }
              .hide-native-add-btn .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 0.4rem !important; }
              .hide-native-add-btn .p-2 { padding: 0.4rem !important; }
              .hide-native-add-btn .p-8, .hide-native-add-btn .p-4 { padding: 1rem !important; }
              .hide-native-add-btn .text-[10px] { font-size: 0.55rem !important; }
              .hide-native-add-btn h3 { font-size: 0.875rem !important; margin-bottom: 0.25rem !important; }
              .hide-native-add-btn .text-center p { font-size: 0.75rem !important; margin-bottom: 0.5rem !important; }
              .hide-native-add-btn .text-center button { display: none !important; }
              .hide-native-add-btn .overflow-y-auto { 
                  min-height: unset !important;
                  height: auto !important;
                  max-height: 450px !important;
              }
              .hide-native-add-btn .text-center {
                  padding-top: 1rem !important;
                  padding-bottom: 1rem !important;
              }
              .hide-native-add-btn .text-gray-500.text-sm,
              .hide-native-add-btn .text-gray-600.text-sm,
              .hide-native-add-btn .text-right.text-sm {
                  font-size: 0.75rem !important;
                  font-weight: 700 !important;
              }
              .hide-native-add-btn .text-gray-500.text-sm svg,
              .hide-native-add-btn .text-gray-600.text-sm svg {
                  width: 0.75rem !important;
                  height: 0.75rem !important;
              }
              .hide-native-add-btn .rounded-full.border-yellow-200 {
                  padding: 0.15rem 0.4rem !important;
                  font-size: 0.65rem !important;
              }
              .hide-native-add-btn .rounded-full.border-yellow-200 svg {
                  width: 0.7rem !important;
                  height: 0.7rem !important;
              }
              .hide-native-add-btn .prose p {
                  font-size: 0.8125rem !important;
                  line-height: 1.2rem !important;
                  margin-top: 0.5rem !important;
              }
              .dark .hide-native-add-btn .prose p {
                  color: #f1f5f9 !important;
              }
              #drawer-scroll-container {
                  scrollbar-width: none !important; /* Firefox */
                  -ms-overflow-style: none !important;  /* IE and Edge */
              }
              #drawer-scroll-container::-webkit-scrollbar {
                  display: none !important; /* Chrome, Safari and Opera */
              }
              .scrollbar-hide {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
              }
              .scrollbar-hide::-webkit-scrollbar {
                  display: none !important;
              }
            `}</style>

                <div className="relative hide-native-add-btn">
                  <NotesTab
                    leadData={lead}
                    isMobile={true}
                    openAddModalExternal={isNoteModalOpen}
                    setOpenAddModalExternal={setIsNoteModalOpen}
                    refreshTrigger={refreshTrigger}
                    hideImportantToggle={true}
                    onRefreshParent={onRefresh}
                    isReadOnly={isDuplicateLead}
                  />
                </div>
              </div>
            )}

            {/* Details Tab */}
            {visitedTabs.has("details") && (
              <div className={activeTab === "details" ? "block mt-0 space-y-2" : "hidden"}>
                <LeadDetailsForm
                  leadData={lead}
                  isEditing={false}
                  setIsEditing={() => { }}
                  onOpenQuickLeadModal={isDuplicateLead ? undefined : () => {
                    setShowLeadEditModal(true);
                  }}
                  leadId={lead?.id || lead?.LeadId || lead?.leadId}
                  hideDescription={true}
                  isReadOnly={isDuplicateLead}
                />
              </div>
            )}

            {/* Timeline Tab */}
            {visitedTabs.has("timeline") && (
              <div className={activeTab === "timeline" ? "block mt-0 space-y-2" : "hidden"}>
                <TimelineView
                  leadId={lead?.id || lead?.LeadId || lead?.leadId}
                  entityName="Lead"
                  scrollTargetId="drawer-scroll-container"
                  isActive={activeTab === "timeline"}
                />
              </div>
            )}

            {/* Application Tab */}
            {!isDuplicateLead && visitedTabs.has("application") && (
              <div className={activeTab === "application" ? "block mt-0 space-y-2" : "hidden"}>
                <ApplicationFormViewTab
                  applicationId={progressData?.ApplicationId}
                  leadUserId={lead?.id || lead?.LeadId || lead?.leadId}
                  leadData={lead}
                  progressData={progressData}
                  isMobile={true}
                  refreshProgressJourney={refreshProgressJourney}
                  onLeadDataRefresh={() => fetchLeadDetails(true)}
                  selectedTab={activeTab === 'application' ? 'application-details' : activeTab}
                />
              </div>
            )}

            {/* Payment Tab */}
            {!isDuplicateLead && visitedTabs.has("payment") && (
              <div className={activeTab === "payment" ? "flex-1 flex flex-col min-h-0 mt-0" : "hidden"}>
                <PaymentDetailsTab
                  isActive={activeTab === "payment"}
                  leadId={lead?.id || lead?.LeadId || lead?.leadId}
                  leadData={lead}
                  isReadOnly={isDuplicateLead}
                  onPaymentSuccess={(formId) => {
                    // Refresh the table in the background
                    if (onRefresh) onRefresh();

                    // If there's an application form to open
                    if (formId) {
                      setSelectedFormForApplication({ FormId: formId });
                      setIsComingFromPaymentFlow(true);
                      setShowQuickLeadModal(true);
                    } else {
                      // If no formId, refresh the current lead's data instead of closing
                      fetchLeadDetails(true);
                      refreshProgressJourney();
                      if (onRefresh) onRefresh();
                    }
                  }}
                />
              </div>
            )}
          </div>

          {activeTab === "notes" && !isDuplicateLead && (
            <div className="px-5 py-2.5 border-t border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-900 flex shrink-0 transition-all duration-300 min-h-[52px] items-center justify-between">
              <div className="flex w-full gap-2 items-center border border-gray-200 dark:border-dark-700 rounded-xl px-2 py-0.5 shadow-sm">
                <div className="pl-2">
                  <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex-1 relative">
                  <textarea
                    onFocus={() => setIsAddingNote(true)}
                    onBlur={() => !newNoteText && setIsAddingNote(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitNote();
                      }
                    }}
                    className="w-full p-2 text-[12px] bg-transparent resize-none transition-all duration-300 outline-none dark:text-dark-100 overflow-hidden"
                    placeholder="Add a note..."
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    style={{ height: isAddingNote || newNoteText ? '52px' : '36px' }}
                  />
                </div>

                <div className="flex flex-row gap-1 items-center pr-1">
                  <button
                    onClick={handleSubmitNote}
                    disabled={isSubmittingNote || !newNoteText.trim()}
                    title="Send Note"
                    className={clsx(
                      "p-1.5 rounded-lg flex items-center justify-center transition-colors",
                      newNoteText.trim() ? "bg-primary-600 text-white hover:bg-primary-700" : "bg-gray-100 text-gray-400 dark:bg-dark-700 dark:text-dark-400"
                    )}
                  >
                    {isSubmittingNote ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent animate-spin rounded-full" />
                    ) : (
                      <svg className="w-3.5 h-3.5 transform rotate-45 -mt-0.5 -ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <CallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Admission Enquiry form",
          stage: "New",
        }}
      />

      <EmailMessageModal
        isOpen={isEmailMessageModal}
        onClose={() => setIsEmailMessageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Admission Enquiry form",
          stage: "New",
        }}
      />

      <SMSMessageModal
        isOpen={isSmsMessageModal}
        onClose={() => setIsSmsMessageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Admission Enquiry form",
          stage: "New",
        }}
      />

      <WhatsAppMessageModal
        isOpen={isWhatsAppMessageModal}
        onClose={() => setIsWhatsAppMessageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Admission Enquiry form",
          stage: "New",
        }}
      />

      <FollowUpScheduleModal
        isOpen={isFollowUpScheduleModal}
        onClose={() => setIsFollowUpScheduleModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          mobile: phoneNumber === "N/A" ? "" : phoneNumber,
          email: emailAddress === "N/A" ? "" : emailAddress,
          source: "Admission Enquiry form",
          stage: "New",
          assignedTo: lead?.assignedTo || lead?.AssignedTo || lead?.LeadUserId
        }}
        onOwnerChange={() => {
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
        onScheduleFollowUp={handleScheduleFollowUp}
      />

      <ApplicationFormModal
        isOpen={showQuickLeadModal && (entityName === "Applicant" || entityName === "Application")}
        onClose={() => {
          setShowQuickLeadModal(false);
          setSelectedFormForApplication(null);
        }}
        leadId={lead?.id || lead?.LeadId || lead?.leadId}
        leadData={lead}
        onLeadUpdated={() => {
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
      />

      <QuickLeadModal
        isOpen={showLeadEditModal || (showQuickLeadModal && entityName !== "Applicant" && entityName !== "Application")}
        onClose={() => {
          setShowLeadEditModal(false);
          setShowQuickLeadModal(false);
          setSelectedFormForApplication(null);
          setIsComingFromPaymentFlow(false);
        }}
        leadId={lead?.id || lead?.LeadId || lead?.leadId}
        categories={lead?.LeadFieldCategories || []}
        selectedForm={selectedFormForApplication}
        isApplicationForm={!!selectedFormForApplication}
        isComingFromPayment={isComingFromPaymentFlow}
        onLeadUpdated={() => {
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();

          // Close the drawer if we just finished an application form
          if (selectedFormForApplication && onClose) {
            onClose();
          }
          // Keep the drawer open after edit as per user request
        }}
      />

      <StageChangeModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId || lead?.leadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          stage: stageName,
          subStage: substageName
        }}
        onStageChange={handlestageUpdate}
      />

      <SetPriorityModal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId || lead?.leadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          priority: lead?.Priority || lead?.priority
        }}
        handlePriorityChange={(priority) => {
          const updateObj = typeof priority === 'object'
            ? priority
            : { priority: priority, Priority: priority };

          if (typeof priority === 'object') {
            setLocalPriority(priority.Priority || priority.priority);
          } else {
            setLocalPriority(priority);
          }

          setLeadDetails(prev => prev ? ({ ...prev, ...updateObj }) : prev);
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
      />

      <SetInteractionLevelModal
        isOpen={showInteractionLevelModal}
        onClose={() => setShowInteractionLevelModal(false)}
        leadData={{
          id: lead?.id || lead?.LeadId || lead?.leadId,
          name: lead?.FullName || lead?.name || lead?.LeadName,
          InteractionLevelName: lead?.InteractionLevelName || lead?.LeadStatus
        }}
        handleInteractionLevelChange={(interactionLevel) => {
          const levelName = typeof interactionLevel === 'object' ? (interactionLevel.InteractionLevelName || interactionLevel.LeadStatus) : interactionLevel;
          setLocalInteractionLevel(levelName);
          setLeadDetails(prev => prev ? ({ ...prev, InteractionLevelName: levelName, LeadStatus: levelName }) : prev);
          setRefreshTrigger(prev => prev + 1);
          if (onRefresh) onRefresh();
        }}
      />

    </div>
  );
}
